import { useEffect, useMemo, useState } from "react"
import { useQuery } from "urql"

import type { Classification, Query } from "@/graphql/codegen/graphql"
import { ClassificationListDocument } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

const LIMIT = 200

interface UseGetProductClassOptionsReturn {
  productClassOptions: Option<string>[]
  isFetching: boolean
}

export const useGetProductClassOptions = (): UseGetProductClassOptionsReturn => {
  const [offset, setOffset] = useState(0)
  const [productClasses, setProductClasses] = useState<Classification[]>([])

  const [{ data, fetching }] = useQuery<Pick<Query, "classification">>({
    query: ClassificationListDocument,
    variables: { input: { limit: LIMIT, offset } },
  })

  const output = data?.classification.list

  useEffect(() => {
    const paginatedProductClasses =
      output?.__typename === "ClassificationListSuccess" ? (output.classifications as Classification[]) : []
    setProductClasses((prev) => (offset === 0 ? paginatedProductClasses : [...prev, ...paginatedProductClasses]))

    if (paginatedProductClasses.length === LIMIT) {
      setOffset((prevOffset) => prevOffset + LIMIT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const productClassOptions = useMemo<Option<string>[]>(
    () =>
      productClasses
        .map((classification) => ({
          value: classification.classificationId,
          label: `${classification.categoryDetail?.name} - ${classification.name}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [productClasses]
  )

  return { productClassOptions, isFetching: fetching }
}
